import React from "react";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import type { InternetEntry, SdwanOwnership, UpTotal } from "../sdwan.types";

type TagVariant = "success" | "error" | "warning" | "default";

/** Map operational/link status to the design-system tag variant. */
export function statusVariant(status: string | null | undefined): TagVariant {
    const s = String(status ?? "").toLowerCase();
    if (["up", "active", "running", "valid"].includes(s)) return "success";
    if (["down", "inactive", "stopped", "expired"].includes(s)) return "error";
    if (["degraded", "warning", "expiring"].includes(s)) return "warning";
    return "default";
}

export function StatusTag({
    status,
    label,
}: {
    status: string | null | undefined;
    label?: string;
}) {
    const text = label ?? (status ? String(status) : "—");
    return (
        <GenericTag
            variant={statusVariant(status)}
            label={text.charAt(0).toUpperCase() + text.slice(1)}
            size="small"
        />
    );
}

/** Ownership pill — TD links render in the brand/success tone (spec §9). */
export function OwnershipTag({ ownership }: { ownership: SdwanOwnership }) {
    const labelMap: Record<SdwanOwnership, string> = {
        td: "Telecom District",
        external: "External ISP",
        internal: "Internal",
        overlay_td: "Overlay (TD)",
        overlay_ext: "Overlay (Ext)",
    };
    const variant: TagVariant =
        ownership === "td" || ownership === "overlay_td"
            ? "success"
            : "default";
    return (
        <GenericTag
            variant={variant}
            label={labelMap[ownership] ?? ownership}
            size="small"
        />
    );
}

/** Health summary tag: all up → success, some down → warning, none → error. */
export function HealthTag({ health }: { health: UpTotal }) {
    if (!health.total) {
        return <GenericTag variant="default" label="—" size="small" />;
    }
    const variant: TagVariant =
        health.up === health.total
            ? "success"
            : health.up === 0
              ? "error"
              : "warning";
    return (
        <GenericTag
            variant={variant}
            label={`${health.up} / ${health.total}`}
            size="small"
        />
    );
}

/**
 * Internet cell as two tags: the up/down status tag plus the interface name in
 * a neutral (default) tag. Used in the hub Associated branches table under the
 * "Status / Interface" sub-header.
 */
export function InternetTagsCell({ entry }: { entry: InternetEntry | null }) {
    if (!entry) {
        return <span className="text-semantic-text-secondary">—</span>;
    }
    return (
        <div className="flex w-full max-w-[160px] items-center gap-2">
            <StatusTag status={entry.status} />
            {entry.interface && (
                <GenericTag
                    variant="default"
                    label={entry.interface}
                    size="small"
                />
            )}
        </div>
    );
}

/** Key/value row used inside the detail cards. */
export function KvRow({
    label,
    children,
}: {
    label: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <div className="flex items-start justify-between gap-4 py-2 border-b border-semantic-border-primary last:border-b-0">
            <span className="text-sm text-semantic-text-secondary">
                {label}
            </span>
            <span className="text-sm text-right">{children}</span>
        </div>
    );
}

export function fmtKbps(kbps: number | null | undefined): string {
    if (kbps == null) return "—";
    const bps = kbps * 1000;
    if (bps >= 1e9) return `${(bps / 1e9).toFixed(1)} Gbps`;
    if (bps >= 1e6) return `${(bps / 1e6).toFixed(1)} Mbps`;
    if (bps >= 1e3) return `${(bps / 1e3).toFixed(0)} Kbps`;
    return `${bps} bps`;
}

export function fmtBytes(n: number | null | undefined): string {
    if (n == null) return "—";
    if (n >= 1e12) return `${(n / 1e12).toFixed(1)} TB`;
    if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(1)} KB`;
    return `${n} B`;
}

export function fmtSpeed(mbps: number | null | undefined): string {
    if (!mbps) return "—";
    return mbps >= 1000 ? `${mbps / 1000} Gbps` : `${mbps} Mbps`;
}

export function relTime(unixSeconds: number | null | undefined): string {
    if (!unixSeconds) return "—";
    const diff = Math.round(Date.now() / 1000 - unixSeconds);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}
